import type { ReactNode } from "react";
import { useState } from "react";

import { Button } from "@/components/ui/button";

export type RunDetailStatus = "通过" | "运行中" | "受限" | "未配置" | "跳过";
export type RunDetailTone = "success" | "warning" | "neutral" | "blocked";

export type RunDetailField = {
	label: string;
	value: string;
	rawName: string;
	source: string;
};

export type RunDetailStage<TId extends string = string> = {
	id: TId;
	title: string;
	status: RunDetailStatus;
	tone: RunDetailTone;
	summary: string;
	primaryFields: RunDetailField[];
	raw: unknown;
};

type RunDetailBadge = {
	label: string;
	tone: RunDetailTone;
};

export function RunDetailWorkspace<TId extends string>({
	headline,
	description,
	statusLabel,
	statusTone,
	badges = [],
	stages,
	activeStageId,
	onSelectStage,
	completeDisclosure,
	summaryTestId = "run-summary-header",
	flowTestId = "run-flow-rail",
	inspectorTestId = "stage-inspector-panel",
}: {
	headline: ReactNode;
	description: ReactNode;
	statusLabel: string;
	statusTone: RunDetailTone;
	badges?: RunDetailBadge[];
	stages: Array<RunDetailStage<TId>>;
	activeStageId: TId;
	onSelectStage: (stageId: TId) => void;
	completeDisclosure: boolean;
	summaryTestId?: string;
	flowTestId?: string;
	inspectorTestId?: string;
}) {
	const activeStage = stages.find((stage) => stage.id === activeStageId) ?? stages[0];

	return (
		<section className="space-y-4 rounded-md border border-surface-border bg-surface-card p-4">
			<div
				className="rounded-md border border-surface-border bg-surface-background p-4"
				data-testid={summaryTestId}
			>
				<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
					<div>
						<div className="text-xs font-medium text-text-muted">运行结论</div>
						<h3 className="mt-1 text-lg font-semibold text-text-primary">{headline}</h3>
						<p className="mt-2 max-w-3xl text-sm leading-6 text-text-secondary">{description}</p>
					</div>
					<div className="flex flex-wrap gap-2">
						<RunDetailStatusPill tone={statusTone}>{statusLabel}</RunDetailStatusPill>
						{badges.map((badge) => (
							<RunDetailStatusPill key={`${badge.label}-${badge.tone}`} tone={badge.tone}>
								{badge.label}
							</RunDetailStatusPill>
						))}
					</div>
				</div>
			</div>

			<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
				<div className="rounded-md border border-surface-border bg-surface-background">
					<div className="border-b border-surface-border px-4 py-3">
						<h3 className="text-base font-semibold text-text-primary">链路拆解主线</h3>
						<p className="mt-1 text-sm text-text-secondary">
							从用户操作到最终回复，按真实后端执行顺序暴露每个环节。
						</p>
					</div>
					<div className="divide-y divide-surface-border" data-testid={flowTestId}>
						{stages.map((stage, index) => (
							<button
								key={stage.id}
								type="button"
								onClick={() => onSelectStage(stage.id)}
								className={`grid w-full gap-3 px-4 py-4 text-left transition md:grid-cols-[42px_minmax(0,1fr)_96px] ${
									stage.id === activeStageId ? "bg-brand-primary/5" : "hover:bg-surface-subtle"
								}`}
							>
								<span className="flex h-8 w-8 items-center justify-center rounded-full border border-surface-border bg-surface-card text-sm font-semibold text-text-secondary">
									{index + 1}
								</span>
								<span>
									<span className="block text-sm font-semibold text-text-primary">
										{stage.title}
									</span>
									<span className="mt-1 block text-sm leading-6 text-text-secondary">
										{stage.summary}
									</span>
									{completeDisclosure ? (
										<span className="mt-2 flex flex-wrap gap-1.5">
											{stage.primaryFields.slice(0, 4).map((field) => (
												<span
													key={`${stage.id}-${field.rawName}`}
													className="rounded border border-surface-border bg-surface-card px-2 py-1 text-xs text-text-secondary"
												>
													{field.label}: {field.value}
												</span>
											))}
										</span>
									) : null}
								</span>
								<span className="md:justify-self-end">
									<RunDetailStatusPill tone={stage.tone}>{stage.status}</RunDetailStatusPill>
								</span>
							</button>
						))}
					</div>
				</div>

				{activeStage ? (
					<StageInspectorPanel
						stage={activeStage}
						completeDisclosure={completeDisclosure}
						testId={inspectorTestId}
					/>
				) : null}
			</div>
		</section>
	);
}

export function RunDetailStatusPill({
	children,
	tone,
}: {
	children: string;
	tone: RunDetailTone;
}) {
	const toneClass =
		tone === "success"
			? "border-brand-success/30 bg-brand-success/10 text-brand-success"
			: tone === "warning"
				? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
				: tone === "blocked"
					? "border-brand-danger/30 bg-brand-danger/10 text-brand-danger"
					: "border-surface-border bg-surface-subtle text-text-secondary";

	return (
		<span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${toneClass}`}>
			{children}
		</span>
	);
}

function StageInspectorPanel<TId extends string>({
	stage,
	completeDisclosure,
	testId,
}: {
	stage: RunDetailStage<TId>;
	completeDisclosure: boolean;
	testId: string;
}) {
	const [showRaw, setShowRaw] = useState(false);
	const visibleFields = completeDisclosure ? stage.primaryFields : stage.primaryFields.slice(0, 6);

	return (
		<aside
			className="rounded-md border border-surface-border bg-surface-background p-4"
			data-testid={testId}
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-xs font-medium text-text-muted">当前选中：{stage.title}</div>
					<p className="mt-2 text-sm leading-6 text-text-secondary">{stage.summary}</p>
				</div>
				<RunDetailStatusPill tone={stage.tone}>{stage.status}</RunDetailStatusPill>
			</div>
			<div className="mt-4 space-y-2">
				{visibleFields.map((field) => (
					<div
						key={`${stage.id}-${field.rawName}-${field.label}`}
						className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2"
					>
						<div className="flex items-start justify-between gap-3">
							<div className="text-sm font-medium text-text-primary">{field.label}</div>
							<div className="text-right text-sm text-text-secondary">{field.value}</div>
						</div>
						<div className="mt-1 text-xs text-text-muted">
							原始字段：{field.rawName} · 来源：{field.source}
						</div>
					</div>
				))}
			</div>
			{stage.primaryFields.length === 0 ? (
				<div className="mt-4 rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-sm text-brand-warning">
					本环节没有返回可展示字段，请查看原始 JSON 暴露契约缺口。
				</div>
			) : null}
			<div className="mt-4 flex flex-wrap gap-2">
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => setShowRaw((value) => !value)}
				>
					查看原始 JSON
				</Button>
				{!completeDisclosure ? (
					<span className="text-xs leading-9 text-text-muted">
						开启完整披露可显示更多字段、来源和原始值。
					</span>
				) : null}
			</div>
			{showRaw ? (
				<pre className="mt-3 max-h-80 overflow-auto rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{JSON.stringify(stage.raw, null, 2)}
				</pre>
			) : null}
		</aside>
	);
}
